import React, { useEffect, useRef } from "react";
import styles from "./portfolioTableCompare.module.scss";
import { useTranslation } from "react-i18next";
import colorsStore from "shared/store/colorsStore";

const FinecoTableCompare = ({
  dataItem,
  dataItem2,
  subSection = false,
  hideTotal = false,
  onResize = (height: number) => {},
}) => {
  const { t } = useTranslation();
  const colorsConfig = colorsStore((state) => state.colorsConfigStore);

  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!containerRef.current) return;

    const handleResize = () => {
      if (onResize) {
        const height = containerRef.current?.offsetHeight || 0;
        onResize(height);
      } else {
        const height = containerRef.current?.offsetHeight || 0;
        const event = new CustomEvent("fineco-table-resize", {
          detail: { height },
        });

        window.dispatchEvent(event);
      }
    };

    const observer = new ResizeObserver(() => {
      handleResize();
    });

    observer.observe(containerRef.current);

    return () => {
      observer.disconnect();
    };
  }, []);

  if (!dataItem || !dataItem.data || !dataItem2 || !dataItem2.data) {
    return null;
  }

  // Use the configuration from dataItem (they should be identical as per your example)
  const configData = dataItem.configData;
  return (
    <div
      ref={containerRef}
      className={`${styles.tableContainer} ${
        subSection ? styles.subSection : ""
      }`}
    >
      <table className={styles.compareTable}>
        <thead>
          <tr>
            <th className={styles.assetClassHeader}>
              <span className={styles.titleHeader}>
                {configData.label_table_left}
              </span>
            </th>
            <th colSpan={2} className={styles.pesoHeader}>
              <span className={styles.titleHeader}>
                {configData.label_table_right}
              </span>
            </th>
          </tr>
          <tr className={styles.subHeaderRow}>
            <th className={styles.emptyCell}></th>
            <th className={styles.portfolioHeader}>
              {t("portafoglioCompare.portafoglio1")}
            </th>
            <th className={styles.portfolioHeader}>
              {t("portafoglioCompare.portafoglio2")}
            </th>
          </tr>
        </thead>
        <tbody>
          {dataItem.data.map((row, index) => {
            // Find matching row in dataItem2 by label
            const matchingRow = dataItem2.data.find(
              (item) => item.label === row.label,
            ) || { weight: 0 };

            // Get display label from config if available
            const displayLabel =
              configData && configData.rows
                ? configData.rows[row.label] || row.label
                : row.label;

            return (
              <tr key={index}>
                <td className={styles.assetClassCell}>{displayLabel}</td>
                <td
                  className={`${styles.valueCell} ${styles.firstPortfolioValue}`}
                >
                  {row.weight.toFixed(2)}
                </td>
                <td className={styles.valueCell}>
                  {matchingRow.weight.toFixed(2)}
                </td>
              </tr>
            );
          })}
          <tr className={styles.totalRow}>
            <td
              className={styles.totalLabel}
              style={{ color: hideTotal ? colorsConfig.FinecoWhite : "" }}
            >
              {t("portfolio.totale")}
            </td>
            <td
              className={`${styles.totalValue} ${styles.firstPortfolioTotal}`}
              style={{ borderColor: hideTotal ? colorsConfig.FinecoWhite : "" }}
            >
              {!hideTotal
                ? Math.round(
                    dataItem.data.reduce((sum, row) => sum + row.weight, 0),
                  )
                : ""}
            </td>
            <td className={styles.totalValue}>
              {!hideTotal
                ? Math.round(
                    dataItem2.data.reduce((sum, row) => sum + row.weight, 0),
                  )
                : ""}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
};

export default FinecoTableCompare;
